export type CompensationMetricReferencePath = 'metric' | 'calculate_over_metric'

export type CompensationMetricReference = {
  nodeId?: string
  nodeType: 'addition' | 'bonus' | 'commission' | 'multiplier'
  path: CompensationMetricReferencePath
  metricId: number
  creditSetupId: number | null
}

type MetricReferenceValue = {
  id?: unknown
  credit_setup_id?: unknown
}

const isMetricReferenceValue = (value: unknown): value is MetricReferenceValue =>
  Boolean(value) && typeof value === 'object'

function toMetricReference(args: {
  nodeId?: string
  nodeType: CompensationMetricReference['nodeType']
  path: CompensationMetricReferencePath
  value: unknown
}): CompensationMetricReference | null {
  if (!isMetricReferenceValue(args.value) || typeof args.value.id !== 'number') {
    return null
  }

  return {
    nodeId: args.nodeId,
    nodeType: args.nodeType,
    path: args.path,
    metricId: args.value.id,
    creditSetupId:
      typeof args.value.credit_setup_id === 'number' ? args.value.credit_setup_id : null
  }
}

/**
 * Extract only calculation Metric references. Target references and JSON Logic
 * Metric variables intentionally do not appear in this dependency projection.
 */
export function extractCompensationMetricReferences(args: {
  nodeId?: string
  nodeType: string
  formData: unknown
}): CompensationMetricReference[] {
  if (!['addition', 'bonus', 'commission', 'multiplier'].includes(args.nodeType)) {
    return []
  }

  const formData = args.formData
  if (!formData || typeof formData !== 'object') return []

  const typedArgs = {
    nodeId: args.nodeId,
    nodeType: args.nodeType as CompensationMetricReference['nodeType']
  }
  const references: CompensationMetricReference[] = []
  const referenceType = (formData as { reference_type?: unknown }).reference_type

  if (referenceType === 'metric') {
    const metricReference = toMetricReference({
      ...typedArgs,
      path: 'metric',
      value: (formData as { metric?: unknown }).metric
    })
    if (metricReference) references.push(metricReference)
  }

  if (args.nodeType === 'commission') {
    const calculatedOverReference = toMetricReference({
      ...typedArgs,
      path: 'calculate_over_metric',
      value: (formData as { calculate_over_metric?: unknown }).calculate_over_metric
    })
    if (calculatedOverReference) references.push(calculatedOverReference)
  }

  return references
}
